import type { Reference, StoreObject } from '@apollo/client'
import { useMutation } from '@apollo/client'
import { toast } from 'sonner'

import { TEAM_MEMBER_FRAGMENT } from '../../../../../../data/fragments'
import { ADD_TEAM_MEMBER, REMOVE_TEAM_MEMBER } from '../../../../../../data/mutations/member'
import { MY_SHIPMENT, WORKSPACE_SHIPMENT } from '../../../../../../data/queries/shipment'
import type { UserDetails } from '../../../../../../types/user'
import { addArrayItemToCache } from '../../../../../../utils/graphql-cache'
import type { AddUserProps } from '../../types'

/**
 * This custom hook is used to handle the mutations for adding and removing team members from a shipment.
 * It allows the user to add and remove team members from a shipment.
 * It also updates the cache with the new data after adding or removing a team member.
 */
export const useTeamMemberMutations = (workspaceID: string | null, shipmentID: string, onError?: () => void) => {
  const customHeaderContext = {
    headers: workspaceID ? { 'ws-id': workspaceID } : {}
  }

  const [addTeamMemberMutation, { loading: addingMemberToShipment }] = useMutation(ADD_TEAM_MEMBER, {
    context: customHeaderContext,
    onCompleted: () => {
      toast.success('Team member added successfully')
    },
    onError: (err) => {
      toast.error(`Failed to add team member: ${err.message}`)
      onError?.()
    },
    update: addArrayItemToCache({
      fieldName: 'teamMembers',
      fragment: TEAM_MEMBER_FRAGMENT,
      id: shipmentID,
      mutationReturnValueFieldName: 'addTeamMember',
      typename: 'Shipment'
    })
  })

  const [removeTeamMember, { loading: removingMemberFromShipment }] = useMutation(REMOVE_TEAM_MEMBER, {
    context: customHeaderContext,
    onCompleted: () => {
      toast.success('Team member removed successfully')
    },
    update: (cache, result, options) => {
      const removedTeamMemberID = result.data?.removeTeamMember?.id ?? options?.variables?.teamMemberID

      if (!removedTeamMemberID) return

      const removeFromTeamMembers = (teamMembers: UserDetails[] | undefined) =>
        teamMembers?.filter((member) => member?.id !== removedTeamMemberID) ?? []

      cache.updateQuery(
        {
          query: MY_SHIPMENT,
          variables: { id: shipmentID }
        },
        (existing) => {
          if (!existing?.myShipment) return existing

          return {
            ...existing,
            myShipment: {
              ...existing.myShipment,
              teamMembers: removeFromTeamMembers(existing.myShipment.teamMembers)
            }
          }
        }
      )

      cache.updateQuery(
        {
          query: WORKSPACE_SHIPMENT,
          variables: { id: shipmentID }
        },
        (existing) => {
          if (!existing?.workspaceShipment) return existing

          return {
            ...existing,
            workspaceShipment: {
              ...existing.workspaceShipment,
              teamMembers: removeFromTeamMembers(existing.workspaceShipment.teamMembers)
            }
          }
        }
      )

      const cacheID = cache.identify({
        __typename: 'Shipment',
        id: shipmentID
      })

      if (!cacheID) return

      cache.modify({
        id: cacheID,
        fields: {
          teamMembers(existingMembers: Reference | readonly (Reference | StoreObject)[] = [], { readField }) {
            if (!Array.isArray(existingMembers)) {
              return existingMembers
            }

            return existingMembers.filter((memberRef) => {
              const memberID =
                readField<string>('id', memberRef as Reference) ??
                (typeof memberRef === 'object' && memberRef && 'id' in memberRef ? memberRef.id : undefined)

              return memberID !== removedTeamMemberID
            })
          }
        },
        optimistic: true
      })
    }
  })

  const handleAddTeamMember = async ({ id, emailOrPhone, user }: AddUserProps) => {
    await addTeamMemberMutation({
      optimisticResponse: {
        addTeamMember: {
          __typename: 'User',
          email: user?.email || null,
          emailOrPhone: emailOrPhone || user?.email || user?.phone || '',
          id,
          name: user?.name || null,
          phone: user?.phone || null,
          photo: user?.photo || null,
          username: user?.username || null
        }
      },
      variables: {
        emailOrPhone,
        id: shipmentID,
        teamMemberID: id
      }
    })
  }

  const handleRemoveTeamMember = async (userID: string) => {
    await removeTeamMember({
      variables: {
        id: shipmentID,
        teamMemberID: userID
      }
    })
  }

  return {
    handleAddTeamMember,
    handleRemoveTeamMember,
    isLoading: addingMemberToShipment || removingMemberFromShipment
  }
}
